import React from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import {
  MARKET_VERDICT_LABELS,
  describeMarketCard,
  describeMarketDuplicates,
  describeMarketSearch,
  describeSimilarCriteria,
  formatMarketNumber,
  formatMarketSignedPct,
  marketNoticeTitle,
  marketReferenceTitle,
  unitBedroomsBucket,
} from '../marketComparison';
import MarketTrendChart from './MarketTrendChart';
import { useMarketTone } from '../marketTone';
import {
  MARKET_FONT,
  MarketBar,
  MarketBarLegend,
  MarketNotice,
  VerdictChip,
  marketSkeletonSx,
} from './MarketParts';

function useMarketStyles() {
  const t = useMarketTone();
  const avoid = t.paper ? { breakInside: 'avoid', pageBreakInside: 'avoid' } : null;
  return {
    block: {
      bgcolor: t.card, border: `1px solid ${t.border}`, borderRadius: '10px', p: 1.75, flexShrink: 0, ...avoid,
    },
    h3: { fontFamily: MARKET_FONT, fontSize: '13px', fontWeight: 600, color: t.head, lineHeight: '16px' },
    big: { fontFamily: MARKET_FONT, fontSize: '20px', fontWeight: 700, color: t.big, lineHeight: 1.2 },
    th: {
      fontFamily: MARKET_FONT, fontSize: '11px', fontWeight: 500, color: t.muted,
      textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', p: '4px 6px',
    },
    td: {
      fontFamily: MARKET_FONT, fontSize: '13px', color: t.text, p: '8px 6px',
      borderTop: `1px solid ${t.line}`, whiteSpace: 'nowrap',
    },
    tdMuted: { color: t.muted },
    baseTag: {
      fontFamily: MARKET_FONT, fontSize: '11px', color: t.accent, bgcolor: t.accentSoft,
      borderRadius: '4px', px: 0.75, py: '1px', ml: 0.75,
    },
    suffix: { fontSize: '13px', fontWeight: 600, color: t.suffix },
    small: { fontFamily: MARKET_FONT, fontSize: '12px', color: t.muted, lineHeight: 1.35 },
    text: { fontFamily: MARKET_FONT, fontSize: '13px', color: t.text, lineHeight: 1.35 },
    mine: t.mine,
    barTrack: t.barTrack,
    barMine: t.barMine,
    barOther: t.barOther,
    accent: t.accent,
  };
}

function SectionTitle({ children, right = null }) {
  const { h3: h3Sx } = useMarketStyles();
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, mb: 1.25 }}>
      <Typography component="h3" sx={h3Sx}>{children}</Typography>
      {right}
    </Box>
  );
}

const suffixSx = { fontSize: '13px', fontWeight: 600 };

/**
 * Número grande del veredicto. Sin «…»: en alquiler (`monthly`) va a 18 px, la moneda y el
 * número no se separan y «/mes» (más chico) puede bajar de línea si la columna es angosta.
 */
function Stat({ label, value, testId, monthly = false }) {
  const { big: bigSx, small: marketSmallSx, suffix: toneSuffix } = useMarketStyles();
  const m = monthly && value ? /^(.*?)(\/mes)$/.exec(value) : null;
  return (
    <Box sx={{ minWidth: 0 }} data-testid={testId}>
      <Typography sx={marketSmallSx}>{label}</Typography>
      {m ? (
        <Typography sx={{ ...bigSx, fontSize: '18px', overflowWrap: 'anywhere' }}>
          {m[1].replace(' ', '\u00A0')}
          <Box component="span" sx={{ ...suffixSx, ...toneSuffix }}>{m[2]}</Box>
        </Typography>
      ) : <Typography sx={monthly ? { ...bigSx, fontSize: '18px' } : bigSx} noWrap>{value || '—'}</Typography>}
    </Box>
  );
}

function VerdictBlock({ block, card }) {
  const { block: blockSx, small: marketSmallSx } = useMarketStyles();
  const basisStats = block.basis === 'similares' && block.marketSimilar ? block.marketSimilar : block.market;
  const n = basisStats?.n ?? 0;
  const confidence = block.confidence ? `Confianza ${block.confidence}` : '';
  const countText = block.basis === 'similares' ? `${n} parecidas` : `${n} en la zona`;
  const medianLabel = block.basis === 'similares' ? 'Mediana de parecidas' : 'Mediana del mercado';
  return (
    <Box sx={blockSx} data-testid="market-report-verdict">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <VerdictChip verdict={block.verdict} label={block.verdictLabel || MARKET_VERDICT_LABELS[block.verdict]} size="lg" testId="market-report-chip" />
        <Typography sx={marketSmallSx}>{[confidence, countText].filter(Boolean).join(' · ')}</Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 2, mt: 1.75 }}>
        <Stat label="Tu unidad" value={card.unitLabel} monthly={card.monthly} testId="market-stat-unit" />
        <Stat
          label={card.monthly ? card.areaLabel : medianLabel}
          value={card.expectedLabel}
          monthly={card.monthly}
          testId="market-stat-median"
        />
        <Stat label="Diferencia" value={formatMarketSignedPct(block.diffPct)} monthly={card.monthly} testId="market-stat-diff" />
      </Box>
      <Box sx={{ mt: 1.75 }}>
        <MarketBar bar={card.bar} verdict={block.verdict} />
        <MarketBarLegend rangeLabel={card.rangeLabel} unitLabel={card.unitLabel} rangePrefix={card.monthly ? 'Rango esperable' : 'Rango medio'} />
      </Box>
      {card.basisNote ? <Typography sx={{ ...marketSmallSx, mt: 1 }}>{card.basisNote}</Typography> : null}
    </Box>
  );
}

function CompareRow({ label, stats, side, isBase, testId }) {
  const { td: tdSx, tdMuted, baseTag: baseTagSx } = useMarketStyles();
  return (
    <Box component="tr" data-testid={testId}>
      <Box component="td" sx={{ ...tdSx, whiteSpace: 'normal' }}>
        {label}
        {isBase ? <Box component="span" sx={baseTagSx}>base</Box> : null}
      </Box>
      <Box component="td" sx={tdSx}>{stats ? stats.n : '—'}</Box>
      <Box component="td" sx={tdSx}>{stats ? formatMarketNumber(stats.median) : '—'}</Box>
      <Box component="td" sx={{ ...tdSx, ...tdMuted }}>
        {stats && stats.p25 !== null && stats.p75 !== null
          ? `${formatMarketNumber(stats.p25)}–${formatMarketNumber(stats.p75)}`
          : '—'}
      </Box>
      <Box component="td" sx={tdSx}>
        {side?.verdict ? (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <span>{formatMarketSignedPct(side.diffPct)}</span>
            <VerdictChip verdict={side.verdict} label={MARKET_VERDICT_LABELS[side.verdict]} size="sm" testId={`${testId}-chip`} />
          </Box>
        ) : '—'}
      </Box>
    </Box>
  );
}

/**
 * Rótulo de unidad de las tablas. Alquiler con superficie: «ARS por mes · 50 m²» (valores =
 * estadística/m² × m² de la unidad). Si no, la métrica de la API («USD/m²», «ARS/m² por mes»).
 */
function marketTableUnitText(block) {
  const m2 = block?.monthly?.areaM2 ? formatMarketNumber(block.monthly.areaM2) : '';
  if (m2) return `${block.currency || 'ARS'} por mes · ${m2} m²`;
  return block?.metricLabel || (block?.operation === 'alquiler' ? 'ARS/m² por mes' : 'USD/m²');
}

function CompareTable({ block }) {
  const { block: blockSx, small: marketSmallSx, th: thSx } = useMarketStyles();
  const criteria = describeSimilarCriteria(block.similarCriteria);
  const unitText = marketTableUnitText(block);
  const m = block.monthly?.areaM2 ? block.monthly : null;
  return (
    <Box sx={blockSx} data-testid="market-report-compare">
      <SectionTitle right={<Typography sx={marketSmallSx}>{unitText}</Typography>}>Parecidas vs. mercado general</SectionTitle>
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <Box component="th" sx={thSx} aria-label="Grupo" />
            <Box component="th" sx={thSx}>Casos</Box>
            <Box component="th" sx={thSx}>Mediana</Box>
            <Box component="th" sx={thSx}>Rango medio</Box>
            <Box component="th" sx={thSx}>Tu precio</Box>
          </tr>
        </thead>
        <tbody>
          <CompareRow
            label="Parecidas a tu unidad"
            stats={m ? m.marketSimilar : block.marketSimilar}
            side={block.similar}
            isBase={block.verdict ? block.basis === 'similares' : false}
            testId="market-row-similar"
          />
          <CompareRow
            label="Mercado general"
            stats={m ? m.market : block.market}
            side={block.general}
            isBase={block.verdict ? block.basis === 'general' : false}
            testId="market-row-general"
          />
        </tbody>
      </Box>
      {criteria ? (
        <Typography sx={{ ...marketSmallSx, mt: 1 }} data-testid="market-similar-criteria">
          {`Parecidas: ${criteria}.`}
        </Typography>
      ) : null}
    </Box>
  );
}

function BedroomsBlock({ block, unit }) {
  const {
    block: blockSx, small: marketSmallSx, text: marketTextSx, mine: mineColor, barTrack, barMine, barOther,
  } = useMarketStyles();
  const rows = (block.monthly?.byBedroomsBasis === 'reales' || block.monthly?.areaM2 ? block.monthly.byBedrooms : null)
    || block.byBedrooms;
  if (!rows?.length) return null;
  let unitText = 'mediana · casos';
  if (block.monthly?.byBedroomsBasis === 'reales') unitText = `${block.currency || 'ARS'} por mes · casos`;
  else if (block.monthly?.areaM2) unitText = `${marketTableUnitText(block)} · casos`;
  const max = Math.max(0, ...rows.map((r) => r.median ?? 0));
  const mine = unitBedroomsBucket(unit?.attributes?.bedrooms);
  return (
    <Box sx={blockSx} data-testid="market-report-bedrooms">
      <SectionTitle right={<Typography sx={marketSmallSx}>{unitText}</Typography>}>Por dormitorios</SectionTitle>
      {rows.map((r) => {
        const isMine = mine !== null && r.bedrooms === mine;
        return (
          <Box key={r.bedrooms} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, my: 0.75 }} data-mine={isMine ? 'true' : undefined} data-testid="market-bedrooms-row">
            <Typography sx={{ ...marketTextSx, width: 130, flexShrink: 0, color: isMine ? mineColor : marketTextSx.color, fontWeight: isMine ? 600 : 400 }} noWrap>
              {isMine ? `${r.label} · tu unidad` : r.label}
            </Typography>
            <Box sx={{ flex: 1, height: 14, bgcolor: barTrack, borderRadius: '3px', position: 'relative' }}>
              <Box sx={{
                position: 'absolute', left: 0, top: 0, bottom: 0, borderRadius: '3px',
                width: `${max > 0 && r.median ? Math.max(4, (r.median / max) * 100) : 0}%`,
                bgcolor: isMine ? barMine : barOther,
              }}
              />
            </Box>
            <Typography sx={{ ...marketTextSx, width: 112, textAlign: 'right', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
              {`${formatMarketNumber(r.median) || '—'} · ${r.n}`}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function TrendBlock({ block }) {
  const { block: blockSx, small: marketSmallSx, accent } = useMarketStyles();
  const t = block.priceTrend;
  if (!t) return null;
  const change = t.changePct !== null && t.changeFromLabel && t.changeToLabel
    ? `${formatMarketSignedPct(t.changePct)} de ${t.changeFromLabel} a ${t.changeToLabel}`
    : '';
  const notes = [
    t.note ? `${t.note.charAt(0).toUpperCase()}${t.note.slice(1)}.` : '',
    t.nominal ? 'En pesos de cada mes, sin ajustar por inflación.' : '',
  ].filter(Boolean);
  return (
    <Box sx={blockSx} data-testid="market-report-trend">
      <SectionTitle right={change ? <Typography sx={{ ...marketSmallSx, color: accent, fontWeight: 600 }} data-testid="market-trend-change">{change}</Typography> : null}>
        Evolución
      </SectionTitle>
      <MarketTrendChart trend={t} />
      <Typography sx={{ ...marketSmallSx, mt: 0.75 }}>
        {[`Mediana ${t.granularity === 'mes' ? 'mensual' : 'trimestral'} del mercado general.`, ...notes].join(' ')}
      </Typography>
    </Box>
  );
}

/**
 * Referencia de la otra moneda (decisión D): casos, mediana y rango medio, sin veredicto ni
 * chips. Solo si la línea tiene mercado. Cada línea en su moneda (nunca se convierten).
 */
function ReferenceBlock({ line }) {
  const { block: blockSx, small: marketSmallSx, th: thSx, td: tdSx, tdMuted } = useMarketStyles();
  if (!line?.market) return null;
  const s = (line.monthly?.areaM2 && line.monthly.market) || line.market;
  const unitText = line.monthly?.areaM2 && line.monthly.market
    ? marketTableUnitText(line)
    : (line.metricLabel || (line.currency === 'USD' ? 'USD/m² por mes' : 'ARS/m² por mes'));
  const cur = line.currency || '';
  return (
    <Box sx={blockSx} data-testid="market-report-reference">
      <SectionTitle right={<Typography sx={marketSmallSx}>{unitText}</Typography>}>
        {marketReferenceTitle(line)}
      </SectionTitle>
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <Box component="th" sx={thSx}>Casos</Box>
            <Box component="th" sx={thSx}>Mediana</Box>
            <Box component="th" sx={thSx}>Rango medio</Box>
          </tr>
        </thead>
        <tbody>
          <tr>
            <Box component="td" sx={tdSx}>{s.n}</Box>
            <Box component="td" sx={tdSx}>{[cur, formatMarketNumber(s.median)].filter(Boolean).join(' ')}</Box>
            <Box component="td" sx={{ ...tdSx, ...tdMuted }}>
              {s.p25 !== null && s.p75 !== null ? `${formatMarketNumber(s.p25)}–${formatMarketNumber(s.p75)}` : '—'}
            </Box>
          </tr>
        </tbody>
      </Box>
      <Typography sx={{ ...marketSmallSx, mt: 1 }}>Solo referencia, sin tipo de cambio.</Typography>
    </Box>
  );
}

/** Skeleton del Resumen: mismos bloques y altos aproximados (sin saltos al cargar o filtrar). */
export function MarketSummarySkeleton() {
  const { block: blockSx } = useMarketStyles();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }} data-testid="market-report-loading" aria-busy="true">
      <Box sx={{ ...blockSx, height: 168 }}>
        <Skeleton variant="rounded" width={200} height={28} sx={{ ...marketSkeletonSx, borderRadius: '999px' }} />
        <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
          {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" width={120} height={40} sx={marketSkeletonSx} />)}
        </Box>
        <Skeleton variant="rounded" height={8} sx={{ ...marketSkeletonSx, mt: 2.5 }} />
      </Box>
      <Box sx={{ ...blockSx, height: 150 }}>
        <Skeleton variant="rounded" width="40%" height={14} sx={marketSkeletonSx} />
        <Skeleton variant="rounded" height={14} sx={{ ...marketSkeletonSx, mt: 2 }} />
        <Skeleton variant="rounded" height={14} sx={{ ...marketSkeletonSx, mt: 1.5 }} />
        <Skeleton variant="rounded" height={14} sx={{ ...marketSkeletonSx, mt: 1.5 }} />
      </Box>
      <Box sx={{ ...blockSx, height: 220 }}><Skeleton variant="rounded" height="100%" sx={marketSkeletonSx} /></Box>
    </Box>
  );
}

/**
 * Pestaña Resumen del informe: veredicto (o un único aviso), parecidas vs. general, por
 * dormitorios y evolución. Nunca muestra la fuente de los datos.
 */
export default function MarketSummary({ data, block, onEdit = undefined }) {
  const { small: marketSmallSx } = useMarketStyles();
  const card = describeMarketCard(data, block);
  const hasVerdict = card.kind === 'verdict';
  const months = block.search?.months;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }} data-testid="market-report-summary">
      {hasVerdict ? <VerdictBlock block={block} card={card} /> : (
        <MarketNotice
          title={marketNoticeTitle(card)}
          text={card.text}
          actionLabel={card.canEdit && onEdit ? 'Editar unidad' : null}
          onAction={onEdit}
        />
      )}
      {block.market ? <CompareTable block={block} /> : null}
      {block.market ? <BedroomsBlock block={block} unit={data.unit} /> : null}
      {block.market ? <TrendBlock block={block} /> : null}
      <ReferenceBlock line={block.otherLine} />
      <Typography sx={{ ...marketSmallSx, textAlign: 'center', pb: 0.5 }} data-testid="market-report-disclaimer">
        {[
          // Búsqueda ampliada por la API (radio o barrio): acá, ya no en la cabecera del modal.
          block.search?.widened ? describeMarketSearch(block.search)
            : (months ? `Publicaciones de la zona de los últimos ${months} meses` : 'Publicaciones de la zona'),
          describeMarketDuplicates(block),
          'No es una tasación.',
        ].filter(Boolean).join(' · ')}
      </Typography>
    </Box>
  );
}
